import { Component, computed, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, firstValueFrom, interval, Observable, Subject, switchMap } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { GAME_ID } from '../global/game-id';
import { FormConfig } from '../admin/config/base-form.config';
import { AppEvent, CoOrganiserDetail } from './models';
import { withDefaultListSlotLabels } from './list-slot-defaults';
import { EventService } from './event.service';
import { UserService } from '../global/userService/user-service';
import { EVENT_FORM_CONFIG } from './event-create.page';
import { ImageService } from '../global/images/image.service';

@Component({
    standalone: false,
    selector: 'abs-event-edit',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.EDIT_TITLE' | translate">
            @if (organiserDisplayName()) {
                <ion-item lines="none">
                    <ion-label>
                        <h3>{{ 'GLOBAL.EVENTS.DETAIL.ORGANISER' | translate }}</h3>
                        <p>{{ organiserDisplayName() }}</p>
                    </ion-label>
                </ion-item>
            }

            <div class="banner-upload-section">
                <p class="banner-label">Event Banner Image <small>(max 2 MB — JPEG, PNG, WebP)</small></p>
                <!-- The shared capture control crops before handing back a blob; the
                     upload stays here so the banner keeps its own organiser-accessible
                     endpoint and its server-side webp/EXIF processing. 3:1 matches the
                     hero strip the banner is shown in. -->
                <abs-photo-capture
                    [currentImageUrl]="bannerPreviewUrl"
                    [uploading]="bannerUploading()"
                    [cropWidth]="1200"
                    [cropHeight]="400"
                    [minZoom]="0.5"
                    [exportFormat]="'jpeg'"
                    (photoSelected)="onBannerCropped($event)"
                    (photoRemoved)="removeBanner()"
                ></abs-photo-capture>
                @if (bannerError) {
                    <p class="banner-error">{{ bannerError }}</p>
                }
            </div>

            <div class="banner-upload-section">
                <p class="banner-label">Event Pack <small>(PDF, max 10 MB)</small></p>
                @if (eventPackFileId) {
                    <div class="banner-actions">
                        <a class="banner-btn" [href]="imageService.url(eventPackFileId)" target="_blank">
                            <ion-icon name="document-outline"></ion-icon>
                            View PDF
                        </a>
                        <button class="banner-btn" (click)="eventPackFileInput.click()" [disabled]="eventPackUploading()">
                            <ion-icon name="document-outline"></ion-icon>
                            Replace
                        </button>
                        <button class="banner-btn banner-btn--remove" (click)="removeEventPack()" [disabled]="eventPackUploading()">
                            <ion-icon name="trash-outline"></ion-icon>
                            Remove
                        </button>
                    </div>
                } @else {
                    <button class="banner-btn banner-btn--add" (click)="eventPackFileInput.click()" [disabled]="eventPackUploading()">
                        @if (eventPackUploading()) {
                            <ion-spinner name="crescent"></ion-spinner>
                            Uploading&hellip;
                        } @else {
                            <ion-icon name="document-outline"></ion-icon>
                            Upload Event Pack
                        }
                    </button>
                }
                <input
                    #eventPackFileInput
                    type="file"
                    accept="application/pdf"
                    style="display:none"
                    (change)="onEventPackSelected($event)"
                />
                @if (eventPackError) {
                    <p class="banner-error">{{ eventPackError }}</p>
                }
            </div>

            <!--
                The status bar. One mechanism, two meanings: on a draft,
                promoting is a checkpoint that stays private; on a published
                event it goes live. Only the wording differs, so the labels are
                chosen by status rather than the bar being duplicated.
            -->
            <div class="event-status-bar">
                <span class="status-state">
                    {{ (isPublished() ? 'GLOBAL.EVENTS.PUBLISH_STATUS.PUBLISHED' : 'GLOBAL.EVENTS.PUBLISH_STATUS.DRAFT') | translate }}
                    @if (pendingChangesEvent()) {
                        &middot;
                        {{ (isPublished() ? 'GLOBAL.EVENTS.STATUS_UNPUBLISHED_EDITS' : 'GLOBAL.EVENTS.STATUS_UNSAVED_EDITS') | translate }}
                    } @else if (lastSavedAt()) {
                        &middot; {{ 'GLOBAL.EVENTS.STATUS_SAVED_AT' | translate: { time: lastSavedAt() } }}
                    }
                </span>
                @if (pendingChangesEvent(); as pending) {
                    <div class="buttons">
                        <ion-button class="btn-sm" color="primary" [disabled]="promoting()" (click)="promoteDraftChanges(pending)">
                            {{ (isPublished() ? 'GLOBAL.EVENTS.PUBLISH_CHANGES_BUTTON' : 'GLOBAL.SAVE') | translate }}
                        </ion-button>
                        <ion-button
                            class="btn-sm"
                            color="medium"
                            fill="outline"
                            [disabled]="promoting()"
                            (click)="discardDraftChanges(pending)"
                        >
                            {{ 'GLOBAL.EVENTS.DISCARD_CHANGES_BUTTON' | translate }}
                        </ion-button>
                    </div>
                }
            </div>

            @if (editFormConfig$ | async; as editFormConfig) {
                <abs-form
                    #eventForm
                    [config]="editFormConfig.config"
                    [value]="editFormConfig.event"
                    (formSubmit)="onSubmit(editFormConfig.eventId, $event.formValue)"
                >
                    <abs-co-organiser-picker
                        [coOrganiserDetails]="coOrganiserDetails"
                        (coOrganisersChange)="coOrganiserIds = $event"
                    ></abs-co-organiser-picker>
                    @if (saveError) {
                        <div class="gutter-padding">
                            <ion-item color="danger" lines="none">
                                <ion-label class="ion-text-wrap">{{ saveError }}</ion-label>
                            </ion-item>
                        </div>
                    }
                    <div class="buttons">
                        <ion-button color="danger" fill="outline" (click)="confirmDelete(editFormConfig.eventId)">
                            {{ 'GLOBAL.EVENTS.DELETE' | translate }}
                        </ion-button>
                    </div>
                </abs-form>
            }
        </abs-page-layout>
    `,
    styles: [
        `
            .banner-upload-section {
                padding: 16px;
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .banner-label {
                margin: 0 0 10px;
                font-weight: 600;
                font-size: 0.85rem;
            }

            .banner-label small {
                font-weight: 400;
                color: var(--ion-color-medium, #92949c);
            }

            /* flex-wrap is what keeps this row inside the section's own padding.
               Without it the three event-pack actions were squeezed flat against
               the right edge at 360px and ran off the page at 320px — the row had
               no padding around it at all on a phone, which is the defect the
               design review was pointed at.
               align-items: center is needed on top of that: the row's default
               (stretch) makes every child's own box match the row's tallest
               sibling, and the "View PDF" <a> — the only anchor among three
               otherwise-identical buttons — doesn't share <button>'s user-agent
               box metrics, so it rendered pinned to the top-left of its own
               stretched box instead of centred in it, reading as "too far up and
               left" against its two <button> siblings. */
            .banner-actions {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 8px;
            }

            /* Metrics taken from abs-photo-capture's .captureBtn, which is the row
               directly above this one on the same form: the two were near-identical
               but differed in padding, gap, weight and size, so the banner actions
               and the event-pack actions did not read as the same control.
               white-space/text-decoration are what stop the "View PDF" anchor
               wrapping onto two lines and carrying a link underline the two
               <button> siblings next to it do not have. */
            .banner-btn {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                padding: 6px 12px;
                border-radius: 6px;
                border: 1px solid var(--ion-color-primary, #3880ff);
                background: var(--ion-color-primary, #3880ff);
                color: #fff;
                font-size: 0.82em;
                font-weight: 600;
                white-space: nowrap;
                text-decoration: none;
                cursor: pointer;
            }

            /* .captureBtn's own icon rule this was matched to also sizes its icon —
               left out the first time, so the "View PDF" icon (this button's only
               ion-icon with no other sizing applied anywhere) rendered at whatever
               the browser default happened to be instead of matching its siblings. */
            .banner-btn ion-icon {
                font-size: 1.15em;
            }

            .banner-btn:disabled {
                opacity: 0.5;
                cursor: default;
            }

            .banner-btn--remove {
                border-color: var(--ion-color-danger, #eb445a);
                background: var(--ion-color-danger, #eb445a);
            }

            .banner-btn--add {
                border-style: dashed;
                background: transparent;
                color: var(--ion-color-primary, #3880ff);
            }

            .banner-error {
                margin: 8px 0 0;
                font-size: 0.82rem;
                color: var(--ion-color-danger, #eb445a);
            }

            .buttons {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
        `
    ]
})
export class EventEditPage implements OnInit, OnDestroy {
    private eventService = inject(EventService);
    private userService = inject(UserService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private alertCtrl = inject(AlertController);
    private translate = inject(TranslateService);
    protected imageService = inject(ImageService);
    gameId = inject(GAME_ID);
    formConfig$ = inject(EVENT_FORM_CONFIG);

    organiserDisplayName = computed(() => this.userService.user()?.displayName);

    editFormConfig$: Observable<{ config: FormConfig; eventId: string; event: AppEvent }>;
    coOrganiserDetails: CoOrganiserDetail[] = [];
    coOrganiserIds: string[] = [];
    saveError: string | null = null;
    deleting = signal(false);
    promoting = signal(false);
    /**
     * The event when it is published and has edits waiting, else null. A draft's
     * edits are its own -- nothing is hidden from anyone -- so the banner is for
     * published events only.
     */
    pendingChangesEvent = signal<AppEvent | null>(null);
    isPublished = signal(false);
    /** Clock time of the last successful autosave, for the status bar. */
    lastSavedAt = signal<string | null>(null);

    @ViewChild('eventForm') private eventForm?: { formGroup?: UntypedFormGroup };
    private currentEvent: AppEvent | null = null;
    private readonly destroyed$ = new Subject<void>();

    /**
     * Thirty seconds, per the spec. Long enough not to chatter, short enough
     * that the most an organiser can lose is half a minute of typing.
     */
    private static readonly AUTOSAVE_INTERVAL_MS = 30_000;

    imageId: string | null = null;
    bannerPreviewUrl: string | null = null;
    bannerUploading = signal(false);
    bannerError: string | null = null;

    eventPackFileId: string | null = null;
    eventPackUploading = signal(false);
    eventPackError: string | null = null;

    private eventLoaded = false;

    ngOnInit() {
        interval(EventEditPage.AUTOSAVE_INTERVAL_MS)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.autosave());

        const event$ = this.route.paramMap.pipe(
            map((params) => params.get('eventId')),
            // The organiser's read, not the public one: the form has to show the
            // edits waiting to be published, not what visitors currently see.
            switchMap((id) => this.eventService.getEventDraft(id)),
            tap((event) => {
                // Outside the eventLoaded guard: the flag has to follow the
                // event on every emission, including the refetch after a
                // promote or discard, not just the first load.
                this.currentEvent = event;
                this.isPublished.set(event.publishStatus === 'published');
                this.pendingChangesEvent.set(event.hasDraftChanges ? event : null);
                if (!this.eventLoaded) {
                    this.eventLoaded = true;
                    this.coOrganiserDetails = event.coOrganiserDetails || [];
                    this.coOrganiserIds = event.coOrganisers || [];
                    this.imageId = event.imageId || null;
                    this.bannerPreviewUrl = event.imageId ? this.imageService.url(event.imageId) : null;
                    this.eventPackFileId = event.eventPackFileId || null;
                }
            })
        );

        this.editFormConfig$ = combineLatest([this.formConfig$, event$]).pipe(
            map(([formConfig, event]) => ({
                config: formConfig,
                eventId: event._id,
                // Fills in any blank list slot label by position ("List n") before the
                // form ever sees it — covers a slot saved before labels were seeded on
                // add, so the organiser doesn't first learn it's unnamed from a save
                // error once labels become required with more than one slot.
                event: { ...event, listSlots: withDefaultListSlotLabels(event.listSlots) }
            }))
        );
    }

    /** Receives the already-cropped blob from abs-photo-capture and stores it. */
    onBannerCropped(blob: Blob) {
        this.bannerError = null;
        this.bannerUploading.set(true);
        this.imageService.uploadEventBanner(this.gameId, blob).subscribe({
            next: (res) => {
                this.imageId = res.id;
                this.bannerPreviewUrl = this.imageService.url(res.id);
                this.bannerUploading.set(false);
            },
            error: (err) => {
                this.bannerError = err?.error?.message || 'Upload failed. Please check the file and try again.';
                this.bannerUploading.set(false);
            }
        });
    }

    removeBanner() {
        this.imageId = null;
        this.bannerPreviewUrl = null;
        this.bannerError = null;
    }

    onEventPackSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        this.eventPackError = null;
        this.eventPackUploading.set(true);
        this.imageService.uploadEventPack(this.gameId, file).subscribe({
            next: (res) => {
                this.eventPackFileId = res.id;
                this.eventPackUploading.set(false);
            },
            error: (err) => {
                this.eventPackError = err?.error?.message || 'Upload failed. Only PDF files up to 10 MB are accepted.';
                this.eventPackUploading.set(false);
            }
        });

        input.value = '';
    }

    removeEventPack() {
        this.eventPackFileId = null;
        this.eventPackError = null;
    }

    onSubmit(eventId: string, formValue: Partial<AppEvent>) {
        this.saveError = null;
        const updates: Partial<AppEvent> = {
            ...formValue,
            coOrganisers: this.coOrganiserIds,
            imageId: this.imageId,
            eventPackFileId: this.eventPackFileId
        };
        this.eventService.updateEvent(eventId, updates).subscribe({
            next: () => {
                // '..' is the manage hub now that edit is nested under it, which is where
                // the organiser came from.
                this.router.navigate(['..'], { relativeTo: this.route });
            },
            error: (err) => {
                this.saveError = err?.error?.message || 'An error occurred while saving. Please check all fields and try again.';
            }
        });
    }

    private async confirmAndRun(titleKey: string, messageKey: string, okKey: string, okCssClass: string | undefined, run: () => void) {
        const alert = await this.alertCtrl.create({
            header: this.translate.instant(titleKey),
            message: this.translate.instant(messageKey),
            buttons: [
                { text: this.translate.instant('GLOBAL.CANCEL'), role: 'cancel' },
                {
                    text: this.translate.instant(okKey),
                    cssClass: okCssClass,
                    handler: () => run()
                }
            ]
        });
        await alert.present();
    }

    async promoteDraftChanges(event: AppEvent) {
        // Both the labels and the copy differ by status. Promoting a draft is a
        // checkpoint nobody else can see; promoting a published event's edits
        // replaces what visitors are looking at. Saying the latter on a draft
        // would be simply untrue.
        const published = this.isPublished();
        await this.confirmAndRun(
            published ? 'GLOBAL.EVENTS.PUBLISH_CHANGES_CONFIRM_TITLE' : 'GLOBAL.EVENTS.SAVE_CHANGES_CONFIRM_TITLE',
            published ? 'GLOBAL.EVENTS.PUBLISH_CHANGES_CONFIRM_MESSAGE' : 'GLOBAL.EVENTS.SAVE_CHANGES_CONFIRM_MESSAGE',
            published ? 'GLOBAL.EVENTS.PUBLISH_CHANGES_BUTTON' : 'GLOBAL.SAVE',
            undefined,
            () => {
                this.promoting.set(true);
                this.eventService.promoteDraftChanges(event._id!).subscribe({
                    next: () => {
                        this.promoting.set(false);
                        this.pendingChangesEvent.set(null);
                        // Back to the event, where the organiser can see what
                        // visitors now see.
                        this.router.navigate(['../..'], { relativeTo: this.route });
                    },
                    error: (err) => {
                        this.promoting.set(false);
                        // The server names the missing fields, which is far more
                        // use than a generic failure.
                        this.saveError = err?.error?.message || this.translate.instant('GLOBAL.EVENTS.PUBLISH_CHANGES_FAILED');
                    }
                });
            }
        );
    }

    async discardDraftChanges(event: AppEvent) {
        await this.confirmAndRun(
            'GLOBAL.EVENTS.DISCARD_CHANGES_CONFIRM_TITLE',
            this.isPublished() ? 'GLOBAL.EVENTS.DISCARD_CHANGES_CONFIRM_MESSAGE' : 'GLOBAL.EVENTS.DISCARD_CHANGES_CONFIRM_MESSAGE_DRAFT',
            'GLOBAL.EVENTS.DISCARD_CHANGES_BUTTON',
            'alert-button-danger',
            () => {
                this.promoting.set(true);
                this.eventService.discardDraftChanges(event._id!).subscribe({
                    next: () => {
                        this.promoting.set(false);
                        this.pendingChangesEvent.set(null);
                        // Reload so the form shows the committed values again.
                        this.router.navigate(['../..'], { relativeTo: this.route });
                    },
                    error: () => {
                        this.promoting.set(false);
                        this.saveError = this.translate.instant('GLOBAL.EVENTS.DISCARD_CHANGES_FAILED');
                    }
                });
            }
        );
    }

    /**
     * Persists the form into the overlay if anything has changed.
     *
     * Deliberately not gated on validity: the overlay is a partial event and is
     * not validated, so a half-typed capacity is storable and the committed
     * value underneath is what protects the event. Validation belongs on
     * promotion, where the button names the missing fields.
     */
    private autosave(): void {
        const group = this.eventForm?.formGroup;
        const event = this.currentEvent;
        if (!group || !event?._id || !group.dirty || this.promoting()) {
            return;
        }
        // Marked pristine before the response, not after: an edit made while
        // the request is in flight must stay dirty so the next tick saves it,
        // rather than being wiped by a late success.
        group.markAsPristine();
        this.eventService
            .updateEvent(event._id, {
                ...group.value,
                coOrganisers: this.coOrganiserIds,
                imageId: this.imageId,
                eventPackFileId: this.eventPackFileId
            })
            .subscribe({
                next: () => {
                    this.lastSavedAt.set(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
                    // There are pending edits now, so the bar gains its actions.
                    this.pendingChangesEvent.set(event);
                },
                error: (err) => {
                    // Dirty again, so the next tick retries rather than the
                    // organiser silently losing the edit.
                    group.markAsDirty();
                    this.saveError = err?.error?.message || this.translate.instant('GLOBAL.EVENTS.AUTOSAVE_FAILED');
                }
            });
    }

    /**
     * Ionic's own leave hook, not ngOnDestroy: a page inside an
     * ion-router-outlet is kept alive in the navigation stack, so ngOnDestroy
     * does not fire when the organiser navigates away and the last edits would
     * never be written.
     */
    ionViewWillLeave(): void {
        this.autosave();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    async confirmDelete(eventId: string) {
        const registrations = await firstValueFrom(this.eventService.listRegistrations(eventId));
        const hasRegistrations = (registrations?.length ?? 0) > 0;
        const messageKey = hasRegistrations
            ? 'GLOBAL.EVENTS.DELETE_CONFIRM_MESSAGE_HAS_REGISTRATIONS'
            : 'GLOBAL.EVENTS.DELETE_CONFIRM_MESSAGE_NO_REGISTRATIONS';

        const alert = await this.alertCtrl.create({
            header: this.translate.instant('GLOBAL.EVENTS.DELETE_CONFIRM_TITLE'),
            message: this.translate.instant(messageKey),
            buttons: [
                { text: this.translate.instant('GLOBAL.CANCEL'), role: 'cancel' },
                {
                    text: this.translate.instant('GLOBAL.EVENTS.DELETE'),
                    cssClass: 'alert-button-danger',
                    handler: () => {
                        this.deleting.set(true);
                        this.eventService.deleteEvent(eventId).subscribe({
                            next: () => {
                                // Three levels, not two: edit now sits at
                                // events/:id/manage/edit, and a deleted event's own detail
                                // page is the one place this must not land.
                                this.router.navigate(['../../..'], { relativeTo: this.route });
                            },
                            error: (err) => {
                                this.deleting.set(false);
                                this.saveError = err?.error?.message || 'Failed to delete event.';
                            }
                        });
                    }
                }
            ]
        });
        await alert.present();
    }
}
